import { motion } from 'motion/react';

import { useTranslation } from '../i18n/hooks';

export default function HowItWorksSection() {
  const t = useTranslation();
  return (
    <motion.section
      id="how-it-works"
      className="overflow-x-hidden bg-white px-4 py-12 sm:px-6 sm:py-16 md:px-8 md:py-20 lg:px-32"
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
      viewport={{ once: true, margin: '-100px' }}
    >
      <div className="mx-auto max-w-6xl">
        {/* Section Header */}
        <motion.div
          className="mb-12 text-center"
          initial={{ y: 50, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          viewport={{ once: true }}
        >
          <motion.h2
            className="mb-4 text-2xl font-bold text-gray-900 sm:text-3xl md:text-4xl"
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            viewport={{ once: true }}
          >
            {t.howItWorks.title}
          </motion.h2>
        </motion.div>

        {/* Two Processes Grid */}
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
          {/* Video Consultation Process */}
          <motion.div
            className="rounded-2xl bg-[#2ab25e] p-6 text-white sm:p-8"
            initial={{ x: -50, opacity: 0 }}
            whileInView={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.8, ease: 'easeOut' }}
            viewport={{ once: true }}
          >
            <motion.h3
              className="mb-6 text-xl font-bold sm:text-2xl"
              initial={{ y: 20, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              viewport={{ once: true }}
            >
              {t.howItWorks.videoConsultation.title}
            </motion.h3>
            <div className="space-y-4">
              {t.howItWorks.videoConsultation.steps.map((step, index) => (
                <motion.div
                  key={index}
                  className="flex items-start gap-4"
                  initial={{ x: -20, opacity: 0 }}
                  whileInView={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.5 + index * 0.1, duration: 0.5 }}
                  viewport={{ once: true }}
                >
                  <motion.div
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/20 text-sm font-bold"
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    transition={{
                      delay: 0.6 + index * 0.1,
                      duration: 0.3,
                      type: 'spring',
                    }}
                    viewport={{ once: true }}
                  >
                    {index + 1}
                  </motion.div>
                  <p className="pt-1 text-base leading-relaxed">{step}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Second Opinion Process */}
          <motion.div
            className="rounded-2xl bg-blue-900 p-6 text-white sm:p-8"
            initial={{ x: 50, opacity: 0 }}
            whileInView={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.8, ease: 'easeOut' }}
            viewport={{ once: true }}
          >
            <motion.h3
              className="mb-6 text-xl font-bold sm:text-2xl"
              initial={{ y: 20, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.6, duration: 0.6 }}
              viewport={{ once: true }}
            >
              {t.howItWorks.secondOpinion.title}
            </motion.h3>
            <div className="space-y-4">
              {t.howItWorks.secondOpinion.steps.map((step, index) => (
                <motion.div
                  key={index}
                  className="flex items-start gap-4"
                  initial={{ x: -20, opacity: 0 }}
                  whileInView={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.7 + index * 0.1, duration: 0.5 }}
                  viewport={{ once: true }}
                >
                  <motion.div
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/20 text-sm font-bold"
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    transition={{
                      delay: 0.8 + index * 0.1,
                      duration: 0.3,
                      type: 'spring',
                    }}
                    viewport={{ once: true }}
                  >
                    {index + 1}
                  </motion.div>
                  <p className="pt-1 text-base leading-relaxed">{step}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Warning Notice */}
        <motion.div
          className="mt-8 rounded-xl border border-amber-200 bg-amber-50 p-4 text-center"
          initial={{ y: 30, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.8, duration: 0.6 }}
          viewport={{ once: true }}
        >
          <p className="text-base font-semibold text-amber-800">
            {t.howItWorks.warning}
          </p>
        </motion.div>
      </div>
    </motion.section>
  );
}
